import dayjs, { Dayjs } from "dayjs"
import { find, orderBy } from "lodash"

import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { Uuid } from "#customTypes/globalTypes"
import { STANDARD_DATE_FORMAT } from "#utils/dateHelpers"
import { getCurrentSemester, isFutureSemester } from "#utils/semesterHelpers"

/** Most recent class first, so the likeliest pick sits at the top. */
export const sortClassesByRecency = (
  classes: ClassConstructor[]
): ClassConstructor[] =>
  orderBy(classes, (cls) => dayjs(cls.startDate).valueOf(), "desc")

/**
 * Default class for an artwork upload: the most recent class in the current
 * semester, falling back to the most recent already-started class. Never a
 * future-semester class — artwork cannot come from a class that has not met.
 * Returns undefined (forcing a manual pick) when every class is in the future.
 */
export const getDefaultUploadClassId = (
  sortedClasses: ClassConstructor[],
  currentSemester: string = getCurrentSemester()
): Uuid | undefined => {
  const currentSemesterClass = find(
    sortedClasses,
    (cls) => cls.semester === currentSemester
  )
  if (currentSemesterClass) return currentSemesterClass.id

  const startedClass = find(
    sortedClasses,
    (cls) => !isFutureSemester(cls.semester, currentSemester)
  )

  return startedClass?.id
}

/**
 * `timeCreated` + `semester` for an artwork record dated by the upload form.
 * Today keeps the real clock (preserves ordering of same-day uploads, matching
 * the pre-date-picker behaviour); a backdated piece lands at noon of that day
 * so timezone drift cannot roll it into a neighbouring day, and its semester
 * is the one the picked date falls in.
 */
export const getArtworkDateFields = (
  pickedDate: Dayjs,
  now: Dayjs,
  realNowMs: number
): { timeCreated: number; semester: string } => {
  const semester = getCurrentSemester(
    pickedDate.format(STANDARD_DATE_FORMAT),
    STANDARD_DATE_FORMAT
  )

  const timeCreated = pickedDate.isSame(now, "day")
    ? realNowMs
    : pickedDate.startOf("day").hour(12).valueOf()

  return { timeCreated, semester }
}
